import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { generatePath, useNavigate } from 'react-router'
import { z } from 'zod'

import { ContractorRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import { queryClient } from '@/lib/queryClient'
import { formatTaxForm } from '@/lib/utils'
import {
  AnimatedFormLabel,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  SlideInScreen,
  Spinner,
  StickyContainer,
  Typography,
} from '@/shared/ui'

import {
  ContractorOnboardingState,
  ContractorOnboardingStepName,
  getContractorTaxForm,
  signTaxForm,
} from '..'

const TAX_FORM_SIGN_FORM_ID = 'tax-form-sign-form-id'

const signContractSchema = z.object({
  signedByFullName: z
    .string()
    .min(1, { message: 'validation.signature.required' }),
})

type SignContractSchema = z.infer<typeof signContractSchema>

type Props = {
  step: ContractorOnboardingStepName
  contractorState?: ContractorOnboardingState
}

export const TaxFormsStep = ({ step, contractorState }: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const taxFormQuery = useQuery({
    queryKey: [queryKeys.getContractorTaxForm],
    queryFn: () =>
      getContractorTaxForm({
        taxFormId: contractorState?.taxFormDetails?.taxFormId ?? '',
      }),
    select: (data) => data.data,
    enabled: !!contractorState?.taxFormDetails?.taxFormId,
  })

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: signTaxForm,
  })

  const onSubmit: SubmitHandler<SignContractSchema> = async (data) => {
    if (!contractorState?.taxFormDetails?.taxFormId) {
      return
    }

    await mutateAsync({
      taxFormId: contractorState.taxFormDetails.taxFormId,
      ...data,
    })

    const currentStepIndex = contractorState.steps.findIndex(
      (s) => s.name === step,
    )

    const nextStep = contractorState?.steps[currentStepIndex + 1]

    await queryClient.invalidateQueries({
      queryKey: [queryKeys.getContractorOnboardingState],
    })

    navigate(
      generatePath(ContractorRoute.OnboardingStep, { step: nextStep.name }),
    )
  }

  const form = useForm<SignContractSchema>({
    mode: 'onChange',
    resolver: zodResolver(signContractSchema),
    values: { signedByFullName: '' },
  })

  if (taxFormQuery.isPending) {
    return (
      <SlideInScreen className="flex items-center justify-center">
        <Spinner className="size-16" />
      </SlideInScreen>
    )
  }

  return (
    <SlideInScreen className="max-w-full">
      <Typography text="center" variant="h3">
        <FormattedMessage
          id="contractor.onboarding.taxForms.title"
          defaultMessage="{taxFormType} tax form"
          values={{ taxFormType: formatTaxForm(taxFormQuery.data?.type) }}
        />
      </Typography>

      <div className="p-2" />

      <Typography text="center">
        <FormattedMessage
          id="contractor.onboarding.taxForms.subtitle"
          defaultMessage="Review carefully and sign your tax form"
        />
      </Typography>

      <div className="p-6" />

      <div className="flex max-h-[400px] flex-col gap-12 overflow-auto rounded-xl">
        {taxFormQuery.data?.base64pages.map((page, index) => (
          <img
            key={index}
            alt={intl.formatMessage(
              {
                id: 'contractor.onboarding.taxForms.title',
                defaultMessage: '{taxFormType} tax form',
              },
              { taxFormType: taxFormQuery.data?.type },
            )}
            src={`data:image/png;base64,${page}`}
            width="100%"
            height="100%"
            className="rounded-xl"
          />
        ))}
      </div>

      <div className="p-6" />

      <Form {...form}>
        <form
          className="mx-auto w-full max-w-xl"
          id={TAX_FORM_SIGN_FORM_ID}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="signedByFullName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="font-(family-name:--font-pacifico)"
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Your signature',
                      id: 'label.yourSignature',
                    })}
                    {...field}
                  />
                </FormControl>
                <AnimatedFormLabel>
                  <FormattedMessage
                    defaultMessage="Your signature"
                    id="label.yourSignature"
                  />
                </AnimatedFormLabel>
              </FormItem>
            )}
          />
        </form>
      </Form>

      <StickyContainer className="mx-auto max-w-xl">
        <Button
          onClick={form.handleSubmit(onSubmit)}
          disabled={isPending || isSuccess}
          loading={isPending || isSuccess}
          width="full"
          id={TAX_FORM_SIGN_FORM_ID}
          type="submit"
        >
          <FormattedMessage
            id="action.applyAndSign"
            defaultMessage="Apply & sign"
          />
        </Button>
      </StickyContainer>
    </SlideInScreen>
  )
}
